/**
 * ParamField factory helpers — reduce boilerplate in strategy definitions.
 *
 * All strike-related params default to step = STRIKE_STEP (5 for SPX).
 * Toggle params use step = 2 (binary) or step = 1 (multi-option).
 */

import { STRIKE_STEP } from "@/lib/constants";
import type { ParamField } from "@/types/strategy";

/** Strike offset param (step defaults to STRIKE_STEP). */
export function offsetParam(
  key: string,
  label: string,
  min: number,
  max: number,
  legIndex?: number,
): ParamField {
  return {
    key,
    label,
    min,
    max,
    step: STRIKE_STEP,
    ...(legIndex != null ? { legIndex } : {}),
  };
}

/** DTE param (step = 1 day). */
export function dteParam(
  key: string,
  label: string,
  min: number,
  max: number,
): ParamField {
  return { key, label, min, max, step: 1 };
}

/** Binary toggle param (two options). */
export function toggleParam(
  key: string,
  label: string,
  options: [[number, string], [number, string]],
): ParamField {
  const [a, b] = options;
  return {
    key,
    label,
    min: a[0],
    max: b[0],
    step: Math.abs(b[0] - a[0]),
    type: "toggle",
    toggleOptions: options,
  };
}

/** Reorder leg-specific controls, keeping shared strategy controls in place. */
export function orderLegParamFields(
  schema: ParamField[],
  legOrder: number[],
): ParamField[] {
  const legFields = schema
    .filter((field) => field.legIndex != null)
    .sort(
      (a, b) => legOrder.indexOf(a.legIndex!) - legOrder.indexOf(b.legIndex!),
    );
  let index = 0;
  return schema.map((field) =>
    field.legIndex == null ? field : legFields[index++],
  );
}
